import { useCallback, useEffect, useRef, useState } from "react";
import type { Constellation, SkyResponse, StarDetailResponse, StarSummary } from "@howcomet/shared";
import { FADING_MARK } from "@howcomet/shared";
import { api, ApiError } from "../api";
import { BigButton } from "../components/BigButton";
import { Bubble } from "../components/Bubble";
import { Check, type CheckResult } from "../shells/registry";
import { labelBelow, layoutConstellation, threadPath } from "../sky-layout";
import { gestureStep } from "../sky-tap";

/**
 * History as a place, not a list. Every finished topic is a star, stars group
 * into constellations by subject, and the whole thing is a picture rather than a
 * taxonomy — which is why a kid will actually open it.
 *
 * Stars dim on their own and show zzz. That is spaced repetition with no
 * vocabulary the child has to learn: they just want the sleepy one to wake up.
 */

/**
 * Where the nth star of a constellation sits, relative to its centre.
 *
 * This replaced a list of eight hand-picked offsets indexed with `i % 8`, which
 * meant a ninth star landed exactly on top of the first — two stars, one point,
 * both labels overprinted. It looked like crowding; it was collision, and it
 * started at nine.
 *
 * The golden angle is what sunflowers use: successive points never line up into
 * spokes, spacing stays even at any count, and because position depends only on
 * the index, a star keeps its place for good as the sky grows around it.
 */
const GOLDEN_ANGLE = Math.PI * (3 - Math.sqrt(5));

function spiralPoint(index: number, spacing: number): [number, number] {
  const angle = index * GOLDEN_ANGLE;
  const radius = spacing * Math.sqrt(index);
  return [Math.cos(angle) * radius, Math.sin(angle) * radius];
}

/**
 * Size and brightness say different things, and separating them is the point.
 *
 * SIZE comes from depth — how far the child explored around this idea. It is
 * earned and never shrinks.
 *
 * BRIGHTNESS comes from strength, which decays. So a big dim star reads as
 * "you went deep here once, and you're forgetting it" — which is exactly the
 * thing worth showing a child, and something neither dimension says alone.
 */
function coreRadius(depth: number): number {
  return 5 + Math.min(depth, 5) * 2.4;
}
function haloRadius(depth: number): number {
  return 15 + Math.min(depth, 5) * 4;
}

export function SkyScreen({
  active,
  onAwarded,
}: {
  /** True while this tab is showing. The screen stays mounted either way. */
  active: boolean;
  onAwarded: (message: string) => void;
}) {
  const [sky, setSky] = useState<SkyResponse | null>(null);
  const [skyBoxRef, skyBox] = useBoxSize<HTMLDivElement>(active);
  const [detail, setDetail] = useState<StarDetailResponse | null>(null);
  /** Set the instant a star is tapped, before the request comes back. */
  const [openingId, setOpeningId] = useState<string | null>(null);
  /**
   * Which constellation fills the screen, or null for the whole sky.
   *
   * Two levels, because the number of categories is fixed: the top level never
   * gets denser however long a child uses this, and only the inside of a
   * constellation grows. Pan and zoom live at the second level only, where the
   * growth actually is — and with a "fit" button, because the risk of free
   * navigation is a child stranded somewhere with no way to tell that is what
   * happened.
   */
  const [openCategory, setOpenCategory] = useState<string | null>(null);
  const [reviewing, setReviewing] = useState(false);
  /** Stars still to work through in a "review the whole thread" run. */
  const [chainQueue, setChainQueue] = useState<string[]>([]);
  const [collecting, setCollecting] = useState(false);
  const [error, setError] = useState("");
  const panelRef = useRef<HTMLDivElement>(null);

  const load = () => {
    api
      .sky()
      .then(setSky)
      .catch((e) => setError(e instanceof ApiError ? e.message : "Couldn't load your sky."));
  };

  /*
    Reload each time this tab is opened rather than once on mount. The screen no
    longer unmounts when the child leaves it — that was losing an in-flight
    question — so a star collected over in the chat would otherwise never show
    up here.
  */
  useEffect(() => {
    if (active) load();
  }, [active]);

  /**
   * Tapping a star opens a panel below the sky, which on most screens is below
   * the fold — so without this the tap looks like it did nothing and the child
   * taps again. Scrolling to the panel is what makes the tap legible.
   *
   * It runs on `openingId`, not on `detail`, so the scroll starts with the
   * placeholder rather than waiting on the network round trip.
   */
  useEffect(() => {
    if (!openingId) return;
    // Deferred by a frame: the tapped star is focusable, so the browser runs
    // its own "scroll the focused element into view" right after the click.
    // Going second means we win.
    const frame = requestAnimationFrame(() => {
      // Instant, not smooth, and deliberately so. `behavior: "smooth"` was
      // observed silently doing nothing in a Chrome build with reduced-motion
      // off and no scroll-behavior override — and if it no-ops in a WKWebView
      // too, the panel goes back to opening off-screen with no sign of it.
      // The entrance animation on the panel supplies the softness; the scroll
      // itself has to be the reliable part. It also means a child sees the
      // panel now rather than after half a second of gliding.
      panelRef.current?.scrollIntoView({ block: "start" });
    });
    return () => cancelAnimationFrame(frame);
  }, [openingId]);

  const open = async (star: StarSummary) => {
    if (openingId === star.topicId) return;
    // Paint the selection and the placeholder first. The request can take a
    // moment, and a tap with no visible consequence reads as broken.
    setReviewing(false);
    setDetail(null);
    setOpeningId(star.topicId);
    try {
      setDetail(await api.star(star.topicId));
    } catch (e) {
      setOpeningId(null);
      setError(e instanceof ApiError ? e.message : "Couldn't open that star.");
    }
  };

  /*
    Reversible, so it asks for no confirmation. A confirm dialog on a kid
    surface teaches that the button is dangerous, and this one is not.
  */
  const letGo = async (topicId: string, starName: string) => {
    closePanel();
    try {
      await api.archiveStar(topicId);
      onAwarded(`"${starName}" has left your sky. Ask about it again to bring it back.`);
      load();
    } catch {
      onAwarded("Couldn't do that just now.");
    }
  };

  const closePanel = () => {
    setDetail(null);
    setOpeningId(null);
    setReviewing(false);
    setChainQueue([]);
  };

  const collect = async (result: CheckResult) => {
    if (!detail || collecting) return;
    setCollecting(true);
    try {
      const outcome = await api.completeCheck(detail.attempt.attemptId, result);
      onAwarded(outcome.message);

      /*
        Keep going through the exploration rather than dropping the child back
        at the sky. Each star still pays and forgets on its own — the chain only
        changes how they are reached, not what they are worth.
      */
      const next = chainQueue.filter((topicId) => topicId !== detail.star.topicId);
      if (next.length > 0) {
        setChainQueue(next);
        setReviewing(false);
        setDetail(null);
        setOpeningId(next[0]!);
        setDetail(await api.star(next[0]!));
        setReviewing(true);
        load();
        return;
      }
      setChainQueue([]);
      closePanel();
      load();
    } catch (e) {
      if (e instanceof ApiError && e.code === "already_claimed") {
        closePanel();
        load();
      } else {
        onAwarded(e instanceof ApiError ? e.message : "Couldn't collect that one.");
      }
    } finally {
      setCollecting(false);
    }
  };

  if (error) return <p className="rise text-cream/70">{error}</p>;
  if (!sky) return <p className="rise text-cream/60">Loading your sky…</p>;

  if (sky.starCount === 0) {
    return (
      <div className="rise pb-32 text-center">
        <p className="disp text-[30px] text-cream">My sky</p>
        <div className="mt-10 text-[56px]">🌌</div>
        <p className="disp mt-4 text-[22px] text-cream">Your sky is empty for now.</p>
        <p className="mt-2 text-cream/60">
          Ask a question and finish the check. That's your first star.
        </p>
      </div>
    );
  }

  /*
    Built once and rendered by both views.

    It used to live inside the whole-sky return only, so once the second level
    shipped, tapping a star inside a constellation set the opening state, scrolled
    to a ref that was not on the page, and showed nothing at all — the review was
    simply unreachable from the only view that shows star names.
  */
  // Anchored whenever a star is opening, not just when the answer has arrived —
  // the scroll effect targets this, so it needs to exist the moment the star is
  // tapped.
  const panel = openingId && (
    <div ref={panelRef} className="pop mt-5 scroll-mt-4">
      {!detail ? (
        <div className="rounded-3xl bg-panel p-5 text-center">
          <div className="twinkle text-[32px]">✨</div>
          <p className="disp mt-2 text-[18px] text-cream/70">Opening your star…</p>
        </div>
      ) : (
        <>
          <div className="rounded-3xl bg-panel p-5">
            <div className="mb-2 flex items-center justify-between">
              <span className="disp text-[15px] uppercase tracking-wide text-pink">
                {detail.star.starName}
                {detail.star.fading && " · fading"}
              </span>
              <button onClick={closePanel} className="kidbtn text-[14px] text-cream/50">
                Close
              </button>
            </div>
            <p className="disp mb-3 text-[22px] text-cream">{detail.star.question}</p>
            <Bubble text={detail.answer} />
          </div>

          {!reviewing ? (
            <div className="mt-3">
              <BigButton
                onClick={() => {
                  setChainQueue([]);
                  setReviewing(true);
                }}
                color={detail.star.fading ? "pink" : "cyan"}
              >
                {detail.star.fading ? "💫 Relight this star" : "🔁 Practise it again"}
              </BigButton>

              {/*
                The reason this exists: a deep star LOOKS like it holds more,
                because size comes from depth — and then one tap gave one
                question. The rest of the exploration was sitting right there as
                separate stars, with no way to walk it.
              */}
              {detail.chain.length > 1 && (
                <button
                  onClick={() => {
                    setChainQueue(detail.chain.map((entry) => entry.topicId));
                    setReviewing(true);
                  }}
                  className="kidbtn disp mt-3 w-full rounded-3xl bg-panel2 px-5 py-4 text-[18px] text-cream"
                >
                  🧵 Practise all {detail.chain.length} in this thread
                </button>
              )}

              {/*
                Quiet, and phrased as letting go rather than deleting, because
                that is what it does — the star leaves the sky and stops asking
                to be relit, and asking the question again brings it straight
                back. Nothing a child can reach erases what they learned.
              */}
              <button
                onClick={() => void letGo(detail.star.topicId, detail.star.starName)}
                className="kidbtn mt-4 w-full rounded-full px-5 py-3 text-[15px] text-cream/45"
              >
                Let this one go
              </button>
            </div>
          ) : (
            <>
              {chainQueue.length > 1 && (
                <p className="mt-3 text-center text-[14px] text-cream/55">
                  Star {detail.chain.length - chainQueue.length + 1} of {detail.chain.length} in this
                  thread
                </p>
              )}
              <Check spec={detail.attempt.spec} onDone={collect} collecting={collecting} />
            </>
          )}
        </>
      )}
    </div>
  );

  const opened = sky.constellations.find((c) => c.category === openCategory) ?? null;

  const enter = (category: string) => {
    closePanel();
    setOpenCategory(category);
  };

  /*
    Leaving takes the open star with it. The panel belongs to a star inside this
    constellation, so leaving it behind meant backing out to the whole sky and
    finding a review still sitting underneath, attached to nothing on screen.
  */
  const leave = () => {
    closePanel();
    setOpenCategory(null);
  };

  /* ---- one constellation, filling the screen ---------------------- */
  if (opened) {
    const points = layoutConstellation(opened.stars);
    const sleepy = opened.stars.filter((star) => star.fading).length;

    return (
      <div className="rise pb-8">
        <p className="disp text-[30px] text-cream">
          {opened.emoji} {opened.name}
        </p>
        <p className="mb-4 mt-1 text-cream/60">
          {opened.stars.length} {opened.stars.length === 1 ? "star" : "stars"}.{" "}
          {sleepy > 0 && (
            <span className="font-extrabold text-pink">
              {sleepy} {sleepy === 1 ? "is" : "are"} fading — tap to relight.
            </span>
          )}
        </p>

        <ConstellationSky
          opened={opened}
          points={points}
          openingId={openingId}
          active={active}
          onOpenStar={(star) => void open(star)}
          onLeave={leave}
        />

        {panel}
      </div>
    );
  }

  /* ---- the whole sky ---------------------------------------------- */
  /*
    The same measured box as a constellation, for the same reason: a grid of at
    most eight categories always fits, so the only question is how much room it
    has been given, not how many rows it needs.
  */
  const columns = skyBox.w >= 860 ? 4 : skyBox.w >= 560 ? 3 : 2;
  const skyRows = Math.max(1, Math.ceil(sky.constellations.length / columns));
  const cellW = skyBox.w / columns;
  const cellH = skyBox.h / skyRows;
  // Star clusters scale with the cell so a four-across grid on a laptop does
  // not draw the same 13px spiral it draws in a two-across column on a phone.
  const clusterSpacing = clamp(Math.min(cellW, cellH) / 11, 8, 18);

  return (
    <div className="rise pb-8">
      <p className="disp text-[30px] text-cream">My sky</p>
      <p className="mb-4 mt-1 text-cream/60">
        {sky.starCount} {sky.starCount === 1 ? "star" : "stars"} in {sky.constellations.length}{" "}
        {sky.constellations.length === 1 ? "constellation" : "constellations"}. Tap one to go in.{" "}
        {sky.fadingCount > 0 && (
          <span className="font-extrabold text-pink">
            {sky.fadingCount} {sky.fadingCount === 1 ? "star is" : "stars are"} fading.
          </span>
        )}
      </p>

      <div
        ref={skyBoxRef}
        style={{ height: skyBox.h }}
        className={`zoom-in ${SKY_BOX} overflow-hidden rounded-3xl bg-[radial-gradient(120%_90%_at_50%_0%,var(--color-night)_0%,var(--color-deep)_70%)]`}
      >
        <svg
          viewBox={`0 0 ${skyBox.w} ${skyBox.h}`}
          className="block h-full w-full"
          role="img"
          aria-label={`Your sky: ${sky.starCount} stars in ${sky.constellations.length} constellations`}
        >
          {/* Background dust. Deterministic, so it doesn't shimmer on re-render. */}
          {Array.from({ length: 70 }, (_, i) => (
            <circle key={i} cx={(i * 97) % skyBox.w} cy={(i * 53) % skyBox.h} r={0.7} fill="var(--color-cream)" opacity={0.18} />
          ))}

          {sky.constellations.map((constellation, index) => {
            const originX = (index % columns) * cellW + cellW / 2;
            // Nudged up inside its cell, because a constellation's two lines of
            // label hang below it and would otherwise sit on the next row.
            const originY = Math.floor(index / columns) * cellH + cellH * 0.42;
            const sleepy = constellation.stars.filter((star) => star.fading).length;

            /*
              No labels on individual stars up here, and that is the whole fix.
              A name under every star is what turned a sky into a wall of text;
              at this level a constellation only has to read as a shape with a
              size, and the names live one tap away.
            */
            return (
              <g
                key={constellation.category}
                onClick={() => enter(constellation.category)}
                onKeyDown={(e) => e.key === "Enter" && enter(constellation.category)}
                className="cursor-pointer"
                role="button"
                tabIndex={0}
                aria-label={`${constellation.name}, ${constellation.stars.length} stars${sleepy > 0 ? `, ${sleepy} fading` : ""}. Open it.`}
              >
                <rect
                  x={(index % columns) * cellW}
                  y={Math.floor(index / columns) * cellH}
                  width={cellW}
                  height={cellH}
                  fill="transparent"
                />

                {constellation.stars.map((star, i) => {
                  const [dx, dy] = spiralPoint(i, clusterSpacing);
                  const strength = star.strength / 100;
                  return (
                    <circle
                      key={star.topicId}
                      cx={originX + dx}
                      cy={originY + dy}
                      r={star.fading ? 2.6 : 3 + Math.min(star.depth, 4) * 0.5}
                      fill={star.fading ? "rgba(255,247,232,.35)" : constellation.color}
                      opacity={star.fading ? 1 : 0.45 + strength * 0.55}
                    />
                  );
                })}

                <text x={originX} y={originY + cellH * 0.34} textAnchor="middle" fill={constellation.color} className="disp" style={{ fontSize: 15 }}>
                  {constellation.emoji} {constellation.name}
                </text>
                <text x={originX} y={originY + cellH * 0.34 + 19} textAnchor="middle" fill="var(--color-cream)" style={{ fontSize: 12, opacity: 0.5 }}>
                  {constellation.stars.length} {constellation.stars.length === 1 ? "star" : "stars"}
                  {sleepy > 0 ? ` · ${sleepy} ${FADING_MARK}` : ""}
                </text>
              </g>
            );
          })}
        </svg>
      </div>

      {panel}
    </div>
  );
}


/* ------------------------------------------------------------------ */
/* the constellation canvas                                            */
/* ------------------------------------------------------------------ */

const MIN_K = 0.35;
const MAX_K = 3.2;

/** Fallback until the box has been measured once. Never seen for long. */
const FALLBACK_BOX = { w: 640, h: 430 };

/** A sky is never shorter than this, nor tall enough to need a page scroll. */
const MIN_SKY_H = 260;
const MAX_SKY_H = 900;

/**
 * The sky's real size in CSS pixels: as wide as its column, as tall as the room
 * left over.
 *
 * The viewBox used to be a fixed 640×430 on an element sized `w-full`, which
 * ties the sky's HEIGHT to the window's WIDTH. On a laptop that made it a
 * thousand pixels tall with the stars stranded in the middle of it; on an iPad
 * in landscape it ran off the bottom of a screen that then had to be scrolled.
 * Neither had anything to do with how many stars there were.
 *
 * The height is worked out by subtraction rather than by adding up furniture:
 * everything in the scroller that is NOT the sky is the content's height minus
 * the sky's own, whatever that happens to be made of. Three attempts to
 * enumerate it failed in three different ways — 300px hardcoded overflowed by
 * exactly 100; measuring the offset missed a second `pb-32` further up the tree
 * and overflowed by exactly 128; `scrollHeight` fixed the overflow and then
 * never grew, because it clamps to the viewport and so cannot see slack. The
 * content element's own height is the one number that is true whether the page
 * overflows or leaves room.
 *
 * Layout properties only, never a bounding rect: this element carries a
 * `zoom-in` animation that scales it from 0.88, so for 400ms its rect is a
 * transform and not a layout, and measuring it there bakes the animation into
 * the height.
 */
function useBoxSize<T extends HTMLElement>(active: boolean) {
  /*
    A callback ref rather than `useRef` + `[]`, because the element this
    measures does not exist when the screen mounts — the sky is still loading,
    and the two views swap their boxes when a child goes in and out of a
    constellation. A ref read once on mount is null forever in the first case
    and stale in the second, which showed up as the whole-sky view keeping its
    fallback height while the constellation view sized itself correctly.
  */
  const [element, setElement] = useState<T | null>(null);
  const ref = useCallback((node: T | null) => setElement(node), []);
  const [box, setBox] = useState(FALLBACK_BOX);

  useEffect(() => {
    const scroller = element?.closest<HTMLElement>("[data-scroller]");
    if (!element || !scroller || !active) return;

    const measure = () => {
      const width = element.clientWidth;
      const content = scroller.firstElementChild as HTMLElement | null;
      if (width < 1 || scroller.clientHeight < 1 || !content) return;
      const everythingElse = content.offsetHeight - element.offsetHeight;
      const height = clamp(scroller.clientHeight - everythingElse, MIN_SKY_H, MAX_SKY_H);
      setBox((previous) =>
        previous.w === width && previous.h === Math.round(height)
          ? previous
          : { w: width, h: Math.round(height) },
      );
    };

    measure();
    /*
      The scroller, not the sky. Watching the sky would be watching the thing
      this resizes — a loop looking for somewhere to happen — and it would also
      re-measure every time a star panel opened underneath, shrinking the sky
      while a child was reading it.
    */
    const observer = new ResizeObserver(measure);
    observer.observe(scroller);
    /*
      A rotation while the child is on another tab resizes nothing here: the
      three screens all stay mounted and the hidden ones measure zero, so this
      would come back to a sky sized for the old orientation. `active` in the
      deps is what re-measures on the way back in; `resize` is belt and braces
      for iOS, where the viewport also changes when the keyboard appears.
    */
    window.addEventListener("resize", measure);
    window.addEventListener("orientationchange", measure);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measure);
      window.removeEventListener("orientationchange", measure);
    };
  }, [element, active]);

  return [ref, box] as const;
}

/** Applied by the hook, so the class only has to hold the floor. */
const SKY_BOX = "min-h-[260px]";

type View = { k: number; x: number; y: number };

/**
 * One constellation, pannable and zoomable.
 *
 * Replaces a canvas that grew taller as stars accumulated. That kept the stars
 * a readable size but turned a picture into a long scroll, and it had no answer
 * at all for width — nineteen stars in one subject overlapped their labels
 * however tall the page got.
 *
 * A fixed window you move the sky inside of is the same thing every map does,
 * and a child already knows how to use it. Pinch, drag, wheel, and buttons all
 * drive one piece of state, so there is no second code path to keep in step.
 */
function ConstellationSky({
  opened,
  points,
  openingId,
  active,
  onOpenStar,
  onLeave,
}: {
  opened: Constellation;
  points: [number, number][];
  openingId: string | null;
  /** False while the child is on another tab: the sky measures zero there. */
  active: boolean;
  onOpenStar: (star: StarSummary) => void;
  onLeave: () => void;
}) {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [boxRef, box] = useBoxSize<HTMLDivElement>(active);
  const [view, setView] = useState<View>({ k: 1, x: box.w / 2, y: box.h / 2 });

  /** Pointers currently down, keyed by pointerId. Client coordinates. */
  /*
    `x`/`y` follow the pointer; `ox`/`oy` remember where it was pressed.

    Both are needed. Panning wants the step since the last event; deciding
    "was that a tap or a drag?" wants the total distance from the press, and
    using the step for that was the bug below.
  */
  const pointers = useRef(new Map<number, { x: number; y: number; ox: number; oy: number }>());
  const pinch = useRef<{ distance: number; k: number } | null>(null);
  /* A drag ends in a click on whatever was under the finger. Without this, a
     child who pans across a star opens it on release. */
  const dragged = useRef(false);

  /** The view that puts every star on screen with room for its label. */
  const fitted = (): View => {
    if (points.length === 0) return { k: 1, x: box.w / 2, y: box.h / 2 };
    const xs = points.map((p) => p[0]);
    const ys = points.map((p) => p[1]);
    // Padding is asymmetric because a label hangs below its star.
    const left = Math.min(...xs) - 60;
    const right = Math.max(...xs) + 60;
    const top = Math.min(...ys) - 40;
    const bottom = Math.max(...ys) + 52;
    const k = clamp(Math.min(box.w / (right - left), box.h / (bottom - top)), MIN_K, 1.25);
    return {
      k,
      x: box.w / 2 - ((left + right) / 2) * k,
      y: box.h / 2 - ((top + bottom) / 2) * k,
    };
  };

  // Refit when the constellation changes, not on every render — a child who
  // has zoomed in should stay where they were while a star panel loads.
  useEffect(() => {
    setView(fitted());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened.category, points.length, box.w, box.h]);

  /** Client pixels to viewBox units. They differ on every screen width. */
  const unitsPerPixel = () => {
    const rect = svgRef.current?.getBoundingClientRect();
    return rect && rect.width > 0 ? box.w / rect.width : 1;
  };

  /** Zoom by `factor`, keeping the point under (cx, cy) exactly where it is. */
  const zoomAt = (factor: number, cx: number, cy: number) => {
    setView((v) => {
      const k = clamp(v.k * factor, MIN_K, MAX_K);
      const scale = k / v.k;
      return { k, x: cx - (cx - v.x) * scale, y: cy - (cy - v.y) * scale };
    });
  };

  /** Zoom from the middle, for the buttons and the keyboard. */
  const zoomCentre = (factor: number) => zoomAt(factor, box.w / 2, box.h / 2);

  /*
    Wheel is bound by hand rather than with onWheel because React attaches
    wheel listeners passively, and a passive listener cannot preventDefault —
    so the page scrolls away underneath while the sky zooms.
  */
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      const rect = svg.getBoundingClientRect();
      const upp = rect.width > 0 ? svg.viewBox.baseVal.width / rect.width : 1;
      // ctrlKey is how a trackpad pinch arrives. It deserves to feel faster
      // than a mouse wheel, because the finger movement is doing more.
      const step = event.ctrlKey ? 0.012 : 0.0025;
      zoomAt(
        Math.exp(-event.deltaY * step),
        (event.clientX - rect.left) * upp,
        (event.clientY - rect.top) * upp,
      );
    };
    svg.addEventListener("wheel", onWheel, { passive: false });
    return () => svg.removeEventListener("wheel", onWheel);
  }, []);

  const onPointerDown = (event: React.PointerEvent<SVGSVGElement>) => {
    /*
      Never track more than two fingers, oldest evicted.

      Not a nicety. A pointer whose `up` never arrives — a system gesture
      interrupting, a finger sliding off the edge of the glass — stays in this
      map forever, and because a pinch reads the first two entries, the sky
      then ignores every real finger until the screen is remounted. Capping and
      evicting means the worst case is one gesture behaving oddly rather than
      the view going dead in a child's hands.
    */
    while (pointers.current.size >= 2) {
      const oldest = pointers.current.keys().next().value;
      if (oldest === undefined) break;
      pointers.current.delete(oldest);
    }
    pointers.current.set(event.pointerId, {
      x: event.clientX,
      y: event.clientY,
      ox: event.clientX,
      oy: event.clientY,
    });
    dragged.current = false;
    if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      pinch.current = { distance: Math.hypot(a!.x - b!.x, a!.y - b!.y), k: view.k };
    }
    /*
      No pointer capture here, and that is the fix.

      Capturing on the SVG retargets the compatibility mouse events: with a
      pointer captured, the browser dispatches `click` at the CAPTURE TARGET
      rather than at the element under the cursor. So the star's own `onClick`
      never ran, no matter how still the mouse was held — the click was
      delivered to the SVG every time. The star still took focus on mousedown,
      drew its focus ring, and did nothing.

      Touch does not behave this way, which is the whole reason the iPad was
      fine and only a browser looked broken.

      Capture is only needed so a DRAG that leaves the SVG keeps panning. It is
      taken when the gesture becomes a drag — see `onPointerMove` — by which
      point there is no click left to protect.
    */
  };

  const onPointerMove = (event: React.PointerEvent<SVGSVGElement>) => {
    const previous = pointers.current.get(event.pointerId);
    if (!previous) return;
    const next = { x: event.clientX, y: event.clientY, ox: previous.ox, oy: previous.oy };
    pointers.current.set(event.pointerId, next);
    const upp = unitsPerPixel();

    if (pointers.current.size >= 2) {

      const [a, b] = [...pointers.current.values()];
      const distance = Math.hypot(a!.x - b!.x, a!.y - b!.y);
      const start = pinch.current;
      if (!start || start.distance === 0) return;
      dragged.current = true;
      const rect = svgRef.current?.getBoundingClientRect();
      if (!rect) return;
      const midX = ((a!.x + b!.x) / 2 - rect.left) * upp;
      const midY = ((a!.y + b!.y) / 2 - rect.top) * upp;
      const target = clamp(start.k * (distance / start.distance), MIN_K, MAX_K);
      setView((v) => {
        const scale = target / v.k;
        return { k: target, x: midX - (midX - v.x) * scale, y: midY - (midY - v.y) * scale };
      });
      return;
    }

    /*
      Was this a tap or a drag? Measured from where the pointer was PRESSED,
      and in screen pixels.

      Both halves of that were wrong before, and the first is what stopped a
      mouse opening a star at all:

      - It compared the step since the last event rather than the distance from
        the press. A mouse reports every pixel it crosses, so an ordinary click
        with three pixels of hand-wobble arrived as one 3px step and was called
        a drag — the click was then swallowed by `onClickCapture` and nothing
        happened. A finger does not do this, because iOS holds back the small
        movements inside its own tap slop, which is why the iPad was fine and
        only the browser looked broken. The same mistake in the other
        direction: a slow drag made of 1px steps never tripped the old test at
        all, so panning gently across the sky then opened whatever star the
        finger happened to stop on.
      - It measured in SKY units, having multiplied by `unitsPerPixel()`. The
        threshold therefore moved with the zoom: far out, a single pixel of
        movement could exceed it; far in, a real drag might not.

      Measured from the press, in screen space, at any zoom — and with a
      larger allowance for a finger than for a mouse. See `TAP_SLOP_PX`.
    */
    const step = gestureStep(
      { x: next.ox, y: next.oy },
      next,
      event.pointerType,
      dragged.current,
    );
    /*
      Capture now, not on press: a drag that wanders off the SVG should keep
      panning, and by this point the gesture is no longer a click.
    */
    if (step.dragging && !dragged.current) {
      try {
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch {
        /* not capturable — dragging still works, it just stops at the edge */
      }
    }
    dragged.current = step.dragging;

    /*
      Nothing moves until the gesture is a drag, and that is the fix rather than
      a refinement of it.

      Panning on every pointermove meant a click with two pixels of wobble slid
      the whole sky by `2 * unitsPerPixel` — which, in a constellation zoomed
      out to fit sixteen stars, is enough to move the star out from under the
      cursor before the button is released. The browser then dispatches `click`
      to the nearest common ancestor of press and release, which is the SVG, and
      the star's own handler never runs. The star still took focus on mousedown,
      so it drew its focus ring and did nothing — exactly what it looked like.

      Recognising the drag was only half of it: the threshold decided whether to
      SWALLOW the click, while the sky moved regardless. Holding the view still
      through the slop window means a tap cannot move its own target.

      The pan also starts from here rather than from the press, so crossing the
      threshold does not jerk the sky by the slop distance.
    */
    if (!step.pan) return;

    const dx = (next.x - previous.x) * upp;
    const dy = (next.y - previous.y) * upp;
    setView((v) => ({ ...v, x: v.x + dx, y: v.y + dy }));
  };

  const endPointer = (event: React.PointerEvent<SVGSVGElement>) => {
    pointers.current.delete(event.pointerId);
    if (pointers.current.size < 2) pinch.current = null;
  };

  const sleepy = opened.stars.filter((star) => star.fading).length;
  const control =
    "kidbtn disp rounded-full bg-panel2/90 text-cream backdrop-blur h-11 w-11 text-[22px] leading-none";

  return (
    <div
      ref={boxRef}
      style={{ height: box.h }}
      className={`zoom-in relative ${SKY_BOX} overflow-hidden rounded-3xl bg-[radial-gradient(120%_90%_at_50%_0%,var(--color-night)_0%,var(--color-deep)_70%)]`}
    >
      {/*
        The way out sits on the sky itself rather than above the heading. It
        reads as leaving this place rather than as page furniture, which is the
        whole reason the sky is a place and not a list.
      */}
      <button
        onClick={onLeave}
        className="kidbtn disp absolute left-3 top-3 z-10 rounded-full bg-panel2/90 px-4 py-2 text-[15px] text-cream backdrop-blur"
      >
        ← All my sky
      </button>

      {/*
        Buttons as well as gestures, and not only for mice. A pinch is
        discoverable on an iPad and invisible on a laptop, and a child who
        cannot find their way back to the whole constellation is stuck in a
        corner of it with no way to tell that is what happened.
      */}
      <div className="absolute right-3 top-3 z-10 flex flex-col gap-2">
        <button onClick={() => zoomCentre(1.35)} className={control} aria-label="Zoom in">
          +
        </button>
        <button onClick={() => zoomCentre(1 / 1.35)} className={control} aria-label="Zoom out">
          −
        </button>
        <button
          onClick={() => setView(fitted())}
          className={`${control} text-[15px]`}
          aria-label="Fit the whole constellation on screen"
        >
          ⤢
        </button>
      </div>

      <svg
        ref={svgRef}
        viewBox={`0 0 ${box.w} ${box.h}`}
        className="block h-full w-full cursor-grab touch-none select-none active:cursor-grabbing"
        role="img"
        aria-label={`${opened.name}: ${opened.stars.length} stars`}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endPointer}
        onPointerCancel={endPointer}
        onLostPointerCapture={endPointer}
        onDoubleClick={() => setView(fitted())}
        onClickCapture={(event) => {
          if (dragged.current) {
            event.stopPropagation();
            dragged.current = false;
          }
        }}
      >
        {/* Background dust sits outside the transform: it is the window, not
            the sky, so it must not slide around when the sky does. */}
        {Array.from({ length: 60 }, (_, i) => (
          <circle
            key={i}
            cx={(i * 97) % box.w}
            cy={(i * 53) % box.h}
            r={0.7}
            fill="var(--color-cream)"
            opacity={0.18}
          />
        ))}

        <g transform={`translate(${view.x} ${view.y}) scale(${view.k})`}>
          {/*
            The lines are the knowledge graph, drawn rather than described. A
            solid line means one question actually led to the next. The faint
            chain that used to hold unrelated stars together is gone: the
            layout groups them now, and a line that means nothing was most of
            what made this view hard to read.
          */}
          {opened.stars.map((star, i) => {
            if (!star.parentTopicId) return null;
            const parent = opened.stars.findIndex((o) => o.topicId === star.parentTopicId);
            if (parent < 0) return null;
            return (
              <path
                key={`thread-${star.topicId}`}
                d={threadPath(points[parent]!, points[i]!, star.topicId)}
                fill="none"
                stroke={opened.color}
                strokeWidth="2"
                strokeLinecap="round"
                opacity="0.55"
                vectorEffect="non-scaling-stroke"
              />
            );
          })}

          {opened.stars.map((star, i) => (
            <Star
              key={star.topicId}
              star={star}
              at={points[i]!}
              color={opened.color}
              selected={openingId === star.topicId}
              onOpen={() => onOpenStar(star)}
              showLabel
              index={i}
              // Labels are drawn at a constant size on screen. Scaling them
              // with the sky makes them unreadable zoomed out and absurd
              // zoomed in, which defeats the reason for zooming either way.
              labelScale={1 / view.k}
            />
          ))}
        </g>
      </svg>

      <p className="pointer-events-none absolute inset-x-0 bottom-2 text-center text-[13px] text-cream/45">
        {sleepy > 0
          ? `Pinch or drag to move around. ${sleepy} ${sleepy === 1 ? "star is" : "stars are"} fading — tap to relight.`
          : "Pinch or drag to move around your sky."}
      </p>
    </div>
  );
}

function clamp(value: number, low: number, high: number): number {
  return Math.min(high, Math.max(low, value));
}

/**
 * One star, drawn the same way wherever it appears.
 *
 * Pulled out when the sky grew a second level: the two views need identical
 * stars, and duplicating forty lines of circles is how the halo and the depth
 * ring end up disagreeing between them.
 */
function Star({
  star,
  at,
  color,
  selected,
  onOpen,
  showLabel,
  index,
  labelScale = 1,
}: {
  star: StarSummary;
  at: readonly [number, number];
  color: string;
  selected: boolean;
  onOpen: () => void;
  showLabel: boolean;
  index: number;
  /** Counters the sky's zoom so a label stays the same size on screen. */
  labelScale?: number;
}) {
  const [x, y] = at;
  const strength = star.strength / 100;
  return (
    <g
      onClick={onOpen}
      className="star-in cursor-pointer"
      style={{ animationDelay: `${Math.min(index, 20) * 0.035}s` }}
      role="button"
      tabIndex={0}
      aria-label={`${star.starName}${star.fading ? ", fading" : ""}`}
      aria-expanded={selected}
      onKeyDown={(e) => e.key === "Enter" && onOpen()}
    >
      {/* Generous invisible hit area — SVG stars are small and a child's
          finger is not. */}
      <circle cx={x} cy={y} r={26} fill="transparent" />
      {/* Marks the tapped star so the finger that pressed it gets an answer,
          even while the panel is still loading and even after the page
          scrolls away from it. */}
      {selected && (
        <circle cx={x} cy={y} r={26} fill="none" stroke={color} strokeWidth="2.5" opacity="0.9" />
      )}
      <circle
        cx={x}
        cy={y}
        r={haloRadius(star.depth)}
        fill={color}
        opacity={0.08 + strength * 0.2}
      />
      <circle
        cx={x}
        cy={y}
        r={coreRadius(star.depth)}
        fill={star.fading ? "rgba(255,247,232,.3)" : color}
        opacity={star.fading ? 1 : 0.45 + strength * 0.55}
      />
      {/* A ring marks a topic explored several questions deep, so depth is
          legible even on a star that has faded. */}
      {star.depth >= 3 && (
        <circle
          cx={x}
          cy={y}
          r={haloRadius(star.depth) + 4}
          fill="none"
          stroke={color}
          strokeWidth="1"
          opacity={0.25 + strength * 0.3}
        />
      )}
      {showLabel && (
        <text
          x={0}
          y={0}
          textAnchor="middle"
          fill="var(--color-cream)"
          style={{ fontSize: 11, opacity: 0.35 + strength * 0.55 }}
          transform={`translate(${x} ${y + (labelBelow(star.topicId) ? 30 : -24) * labelScale}) scale(${labelScale})`}
        >
          {star.starName}
        </text>
      )}
      {star.fading && (
        <text x={x + 12} y={y - 10} style={{ fontSize: 14 }}>
          {FADING_MARK}
        </text>
      )}
    </g>
  );
}
